/**
 * Recorded-note upload client (node N2) for a recorded note (video/screen or voice)
 * or a video the host picked. Given the `Blob`, it runs the presigned direct-to-R2 flow
 * N1 exposes:
 *   `api.notes.start` → `api.notes.sign` → PUT each part **straight to R2** (read the
 *   ETag off the response) → `api.notes.complete` → poll `api.notes.get` until ready.
 * On success it returns the `Attachment` the composer persists plus the gated preview
 * URLs (media/poster with the `?t=` media token) for immediate in-app playback.
 *
 * The parts go through the webinar recording's own engine (`uploadPartsWithRecovery`
 * in videoUpload.ts) with the notes sign and complete calls: signed a batch at a
 * time, six in flight, each retried on a fresh signature, and a failure mid-way
 * picked back up from the receipts already earned. A 10 GB video uploads as fast
 * as a webinar's recording does.
 * The R2 PUT uses the injectable `fetchImpl` (defaults to the global `fetch`), so the
 * whole flow is unit-testable with a single mocked fetch (see test/note-upload.test.ts).
 */
import type {
  Attachment,
  NoteKind,
  NoteStatusResponse,
  NoteUploadCompleteRequest,
  NoteUploadAbortRequest,
  NoteUploadCompleteResponse,
  NoteUploadSignRequest,
  NoteUploadSignResponse,
  NoteUploadStartRequest,
  NoteUploadStartResponse,
} from "../../shared/types";
import { MAX_NOTE_BYTES, NOTE_TOO_BIG_MSG } from "../../shared/config";
import { api, ApiError } from "./api";
import type { LiveNoteUpload } from "./liveNoteUpload";
import { type PartUploadCalls, planParts, uploadPartsWithRecovery } from "./videoUpload";

/** The recorded note, ready to attach + preview. */
interface RecordedNote {
  /** The note the upload minted; the offer card keeps this to find its video again. */
  noteId: string;
  /** The attachment the composer stores (`media_key`/`poster_key` are R2 keys). */
  attachment: Attachment;
  /** Gated playback URL for immediate preview (`media_url` + `?t=<token>`). */
  previewUrl: string | null;
  /** Gated poster URL for immediate preview (video notes only). */
  posterUrl: string | null;
  durationSec: number;
}

/** Where a note's upload calls go: the host's own notes, or a person's page by its token. */
export interface NoteApi {
  start: (body: NoteUploadStartRequest) => Promise<NoteUploadStartResponse>;
  sign: (body: NoteUploadSignRequest) => Promise<NoteUploadSignResponse>;
  complete: (body: NoteUploadCompleteRequest) => Promise<NoteUploadCompleteResponse>;
  abort: (body: NoteUploadAbortRequest, opts?: { keepalive?: boolean }) => Promise<unknown>;
  get: (noteId: string) => Promise<NoteStatusResponse>;
}

/** A video the person records on their page goes through their page's own note routes. */
export function pageNoteApi(token: string): NoteApi {
  return {
    start: (body) => api.watch.notes.start(token, body),
    sign: (body) => api.watch.notes.sign(token, body),
    complete: (body) => api.watch.notes.complete(token, body),
    abort: (body, opts) => api.watch.notes.abort(token, body, opts),
    get: (noteId) => api.watch.notes.get(token, noteId),
  };
}

interface RecordNoteOptions {
  /** The upload calls; the host's own notes when left out. */
  notes?: NoteApi;
  /** The attachment display name (e.g. "Camera reply", "Voice note"). */
  name?: string;
  /** Make it an adaptive stream whatever its length: a registration page's video. */
  stream?: boolean;
  /** The take's parts already on their way or in R2, from the recorder; only Send is left. */
  live?: LiveNoteUpload | null;
  /** Coarse phase for the recorder's status line. */
  onPhase?: (phase: "uploading" | "processing") => void;
  /** Upload progress across the parts (0–100). */
  onProgress?: (pct: number) => void;
  signal?: AbortSignal;
  /** Swap the transport used for the R2 part PUTs (tests). Defaults to `fetch`. */
  fetchImpl?: typeof fetch;
  /** Poll cadence + budget while the note transcodes. */
  pollIntervalMs?: number;
  pollTries?: number;
  /** Retries per part before giving up on a transient network error. Default 5. */
  maxRetriesPerPart?: number;
  /** Swap the backoff sleep between retries (tests). Defaults to a real wait. */
  delayImpl?: (ms: number) => Promise<void>;
}

/** `M:SS` clock label for a duration in seconds. */
export function fmtClock(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** R2 key of the transcoded note file — the middle path segment is the note id. */
export function noteMediaKey(noteId: string, kind: NoteKind): string {
  return `notes/${noteId}/note.${kind === "voice" ? "m4a" : "mp4"}`;
}
/** R2 key of a video note's poster JPEG. */
export function notePosterKey(noteId: string): string {
  return `notes/${noteId}/poster.jpg`;
}

export function withToken(url: string | null, token: string | null): string | null {
  if (!url) return null;
  // Why: an address that already carries its key never gets a second one.
  if (!token || /[?&]t=/.test(url)) return url;
  return `${url}${url.includes("?") ? "&" : "?"}t=${encodeURIComponent(token)}`;
}

/**
 * Turn a `ready` note status into the attachment + preview URLs. Pure. The keys the
 * server names win (a long video is a stream, `hls/<id>/master.m3u8`); without them
 * they are derived from the reserve's id and kind, never from the URL.
 */
export function buildNoteAttachment(
  start: Pick<NoteUploadStartResponse, "note_id" | "kind">,
  status: NoteStatusResponse,
  name: string,
): RecordedNote {
  const { note_id, kind } = start;
  const attachment: Attachment = {
    kind,
    name,
    media_key: status.media_key ?? noteMediaKey(note_id, kind),
  };
  if (status.duration_sec > 0) attachment.duration = fmtClock(status.duration_sec);
  const posterKey =
    status.poster_key ?? (kind === "video" && status.poster_url ? notePosterKey(note_id) : null);
  if (posterKey) attachment.poster_key = posterKey;
  return {
    noteId: note_id,
    attachment,
    previewUrl: withToken(status.media_url, status.media_token),
    posterUrl: withToken(status.poster_url, status.media_token),
    durationSec: status.duration_sec,
  };
}

/** The notes sign and complete calls, for the shared part engine. `size` rides on complete, where it is checked. */
export function noteCalls(
  start: Pick<NoteUploadStartResponse, "note_id" | "upload_id">,
  notes: NoteApi,
  size?: number,
): PartUploadCalls<NoteUploadCompleteResponse> {
  const ref = { note_id: start.note_id, upload_id: start.upload_id };
  return {
    sign: async (part_numbers) => (await notes.sign({ ...ref, part_numbers })).urls,
    complete: (parts) => notes.complete({ ...ref, parts, ...(size === undefined ? {} : { size }) }),
  };
}

/** How often the page asks whether the note is ready. */
const POLL_INTERVAL_MS = 2000;
/** The shortest wait for a note to be made playable. */
const POLL_MIN_MS = 5 * 60 * 1000;
/**
 * The wait for each gigabyte on top. Why: a video under five minutes, or one
 * whose length cannot be read, is still encoded whole on one container, so a
 * heavy file can take hours, and giving up while it still works would tell the
 * host it failed when it did not.
 */
const POLL_PER_GB_MS = 20 * 60 * 1000;

/** How many polls a note of this size gets before the page gives up on it. */
export function pollTriesFor(bytes: number, intervalMs = POLL_INTERVAL_MS): number {
  const waitMs = POLL_MIN_MS + (Math.max(0, bytes) / 1024 ** 3) * POLL_PER_GB_MS;
  return Math.ceil(waitMs / Math.max(1, intervalMs));
}

/** Poll `GET /api/notes/:id` until ready / error, or a bounded timeout. */
async function pollNoteReady(
  noteId: string,
  {
    intervalMs = POLL_INTERVAL_MS,
    tries = pollTriesFor(0),
    signal,
    notes = api.notes,
  }: { intervalMs?: number; tries?: number; signal?: AbortSignal; notes?: NoteApi } = {},
): Promise<NoteStatusResponse> {
  let last: NoteStatusResponse | null = null;
  for (let i = 0; i < tries; i++) {
    if (signal?.aborted) throw new ApiError(0, "Recording upload canceled.");
    last = await notes.get(noteId);
    if (last.ready || last.state === "error") return last;
    if (intervalMs > 0) await new Promise((r) => setTimeout(r, intervalMs));
  }
  return (
    last ?? {
      note_id: noteId,
      kind: "video",
      ready: false,
      state: "error",
      duration_sec: 0,
      media_url: null,
      poster_url: null,
      media_token: null,
      media_key: null,
      poster_key: null,
    }
  );
}

/**
 * The full recorded-note flow: reserve → the part engine (sign, PUT each part direct
 * to R2, complete) → poll until the note is made playable → the attachment.
 * `onPhase`/`onProgress` drive the recorder's status line.
 */
export async function recordNote(
  kind: NoteKind,
  blob: Blob,
  opts: RecordNoteOptions = {},
): Promise<RecordedNote> {
  const name = opts.name ?? (kind === "voice" ? "Voice note" : "Camera reply");

  if (blob.size > MAX_NOTE_BYTES) throw new ApiError(0, NOTE_TOO_BIG_MSG);

  const notes = opts.notes ?? api.notes;
  opts.onPhase?.("uploading");
  const partOptions = {
    fetchImpl: opts.fetchImpl,
    signal: opts.signal,
    maxRetriesPerPart: opts.maxRetriesPerPart,
    delayImpl: opts.delayImpl,
  };
  const live = opts.stream ? null : await opts.live?.take();
  let start: NoteUploadStartResponse | null = null;
  if (live) {
    // Why the whole file again on a failure: a take that began while recording can be cut off (an expired
    // reserve, a lost connection); the person's file is still here, so it goes up as any file does.
    const total = planParts(blob.size, live.start.part_size).length;
    opts.onProgress?.((live.receipts.length / total) * 100);
    try {
      await uploadPartsWithRecovery(blob, live.start.part_size, noteCalls(live.start, notes, blob.size), {
        ...partOptions,
        resumeFrom: live.receipts,
        onPartComplete: (_part, done, all) => opts.onProgress?.((done / all) * 100),
      });
      start = live.start;
    } catch (err) {
      await opts.live?.abort();
      if (opts.signal?.aborted) throw err;
    }
  }
  opts.live?.done();
  if (!start) {
    start = await notes.start({
      kind,
      content_type: blob.type || undefined,
      size: blob.size,
      ...(opts.stream ? { stream: true } : {}),
    });
    await uploadPartsWithRecovery(blob, start.part_size, noteCalls(start, notes, blob.size), {
      ...partOptions,
      onPartComplete: (_part, done, total) => opts.onProgress?.((done / total) * 100),
    });
  }

  opts.onPhase?.("processing");
  const intervalMs = opts.pollIntervalMs ?? POLL_INTERVAL_MS;
  const status = await pollNoteReady(start.note_id, {
    intervalMs,
    tries: opts.pollTries ?? pollTriesFor(blob.size, intervalMs),
    signal: opts.signal,
    notes,
  });
  if (!status.ready) throw new ApiError(0, "That recording could not be processed. Try again.");

  return buildNoteAttachment(start, status, name);
}

